// YERİ: src/app/domains/site/sections/menu-bar.section.ts
// Menü — İÇERİK widget'ı. Global header'ı BASTIRMAZ; sayfanın herhangi bir
// yerine bırakılır ve yatay VEYA dikey menü olarak çizilir. Veri "Menüler"
// sayfasındaki ClientWebMenu'den gelir (header/footer location) — tek kaynak,
// parent/child + hover alt menü bedavaya gelir.
// config: { location, direction (horizontal|vertical), align, sticky }

import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { TenantWebService, SiteMenuItem } from '../core/tenant-web.service';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'sec-menu-bar',
  standalone: true,
  imports: [TranslocoPipe],
  template: `
    <nav class="border-b border-black/5 bg-white dark:border-white/10 dark:bg-neutral-950"
         [class.sticky]="sticky()" [class.top-0]="sticky()" [class.z-30]="sticky()">
      <div class="mx-auto flex max-w-7xl px-6 py-3 lg:px-8"
           [class.flex-row]="!vertical()" [class.flex-wrap]="!vertical()" [class.items-center]="!vertical()"
           [class.gap-x-7]="!vertical()" [class.gap-y-1]="!vertical()"
           [class.flex-col]="vertical()" [class.gap-1]="vertical()"
           [class.justify-start]="!vertical() && align() === 'left'"
           [class.justify-center]="!vertical() && align() === 'center'"
           [class.justify-end]="!vertical() && align() === 'right'"
           [class.items-start]="vertical() && align() === 'left'"
           [class.items-center]="vertical() && align() === 'center'"
           [class.items-end]="vertical() && align() === 'right'">
        @for (m of menu(); track m.id) {
          @if (m.children?.length) {
            <div class="group relative">
              <a [href]="m.url" [target]="m.openInNewTab ? '_blank' : '_self'"
                 class="inline-flex cursor-pointer items-center text-sm font-medium text-neutral-600 transition hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white">{{ m.title }}</a>
              <!-- Yatay → altta açılır, Dikey → yana açılır -->
              <div class="invisible absolute z-50 min-w-[190px] opacity-0 transition-all group-hover:visible group-hover:opacity-100"
                   [class.left-0]="!vertical()" [class.top-full]="!vertical()" [class.pt-2]="!vertical()"
                   [class.left-full]="vertical()" [class.top-0]="vertical()" [class.pl-2]="vertical()">
                <div class="rounded-xl border border-black/5 bg-white py-2 shadow-xl dark:border-white/10 dark:bg-neutral-900">
                  @for (c of m.children!; track c.id) {
                    <a [href]="c.url" [target]="c.openInNewTab ? '_blank' : '_self'"
                       class="block px-4 py-2 text-sm text-neutral-600 transition hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-white">{{ c.title }}</a>
                  }
                </div>
              </div>
            </div>
          } @else {
            <a [href]="m.url" [target]="m.openInNewTab ? '_blank' : '_self'"
               class="text-sm font-medium text-neutral-600 transition hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white">{{ m.title }}</a>
          }
        }
        @if (preview() && menu().length === 0) {
          <span class="text-sm text-neutral-400">{{ 'siteEditor.emptyMenu' | transloco: { value: location() } }}</span>
        }
      </div>
    </nav>
  `,
})
export class MenuBarSection {
  private svc = inject(TenantWebService);
  private text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  clientId = input<number>(0);
  /** Editör önizlemesinde boş menü için ipucu göster. */
  preview = input<boolean>(false);

  protected menu = signal<SiteMenuItem[]>([]);
  private lastKey = '';

  /** Hangi menü gösterilsin (Menüler sayfasındaki location): header | footer. */
  protected location = computed(() => cfg<string>(this.config(), 'location', 'header'));
  /** Yön: horizontal (yatay) | vertical (dikey). */
  protected vertical = computed(() => cfg<string>(this.config(), 'direction', 'horizontal') === 'vertical');
  protected align = computed(() => cfg<string>(this.config(), 'align', 'center'));
  /** Sticky (sayfa kayarken üstte sabit). config 'true' / 'evet' / '1' → açık. */
  protected sticky = computed(() => ['true', 'evet', '1'].includes(String(cfg(this.config(), 'sticky', '')).toLowerCase()));

  /**
   * Kaynak (şirket + konum) VEYA dil değişince menü yeniden çekilir. Dil de
   * anahtarın parçası: başlıklar çevriliyor, aynı konumun Fransızcası ayrı
   * bir sonuç.
   */
  constructor() {
    effect(() => {
      const id = this.clientId();
      if (!id) return;
      const loc = this.location();
      const dil = this.icerikDili();
      const key = id + '|' + loc + '|' + (dil ?? '');
      if (key === this.lastKey) return;   // aynı kaynak → tekrar çekme
      this.lastKey = key;
      this.svc.getMenu(id, loc, dil).subscribe({ next: m => this.menu.set(m ?? []), error: () => {} });
    });
  }

  /** Varsayılan dilde null gider — sunucu o durumda çeviri sorgusu atmaz. */
  private icerikDili(): string | null {
    const dil = this.text.lang();
    return dil && dil !== this.text.varsayilanDil() ? dil : null;
  }
}
